import { useEffect, useState } from "react";
import { MapPin, LocateFixed, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Input } from "./ui/input";

export default function ShopAddressFields({ shop, setShop, errors }) {
  const [areas, setAreas] = useState([]);
  const [busy, setBusy] = useState("");
  const [pinOk, setPinOk] = useState(false);
  const set = (k) => (e) => setShop({ ...shop, [k]: k === "pincode" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value });

  useEffect(() => {
    setPinOk(false);
    if (shop.pincode.length !== 6) return setAreas([]);
    setBusy("pin");
    api.get(`/geo/pincode/${shop.pincode}`)
      .then((r) => { setPinOk(true); setAreas(r.data.areas); setShop((s) => ({ ...s, city: r.data.city || s.city, district: r.data.district, state: r.data.state, area: r.data.areas.includes(s.area) ? s.area : r.data.areas[0] })); })
      .catch((e) => { setAreas([]); setShop((s) => ({ ...s, district: "", state: "", area: "" })); toast.error(errMsg(e)); })
      .finally(() => setBusy(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop.pincode]);

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location is not available on this device");
    setBusy("gps");
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const { data } = await api.get("/geo/reverse", { params: { lat: coords.latitude, lng: coords.longitude } });
        setShop((s) => ({ ...s, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== "" && v != null)), address: data.address || s.address }));
        toast.success("Address filled from your location. Please check it.");
      } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
    }, (err) => { setBusy(""); toast.error(err.code === 1 ? "Location permission is blocked. Allow it in your browser settings, or type your PIN code" : "Could not get your location. Turn on GPS and try again, or type your PIN code"); }, { enableHighAccuracy: false, timeout: 20000, maximumAge: 120000 });
  };

  const Err = ({ k }) => (errors[k] ? <p data-testid={`seller-${k}-error`} className="text-xs text-red-400 mt-1">{errors[k]}</p> : null);
  const found = pinOk && busy !== "pin";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="seller-shop-address">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold flex items-center gap-2"><MapPin size={15} className="text-[#00f0ff]" />Shop address</p>
        <button type="button" data-testid="seller-use-location-btn" onClick={locate} disabled={!!busy} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00f0ff]/40 text-[#00f0ff] hover:bg-[#00f0ff]/10 disabled:opacity-50">
          {busy === "gps" ? <Loader2 size={13} className="animate-spin" /> : <LocateFixed size={13} />}Use my current location
        </button>
      </div>
      <div className="relative">
        <Input data-testid="seller-pincode-input" inputMode="numeric" placeholder="Shop PIN code (6 digits)" value={shop.pincode} onChange={set("pincode")} className="h-11 pr-9" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">{busy === "pin" ? <Loader2 size={15} className="animate-spin text-slate-400" /> : found ? <CheckCircle2 size={15} className="text-emerald-400" /> : null}</span>
        <Err k="pincode" />
      </div>
      {found && <p data-testid="seller-pincode-found" className="text-xs text-emerald-400">{[shop.city, shop.district, shop.state].filter(Boolean).join(", ")}</p>}
      {areas.length > 0 && (
        <select data-testid="seller-area-select" value={shop.area} onChange={set("area")} className="w-full h-11 bg-transparent border border-input rounded-md px-3 text-sm">
          {areas.map((a) => <option key={a} value={a} className="bg-[#12141c]">{a}</option>)}
        </select>
      )}
      <div><Input data-testid="seller-address-input" placeholder="Shop no., building, street" value={shop.address} onChange={set("address")} className="h-11" /><Err k="address" /></div>
      <div><Input data-testid="seller-landmark-input" placeholder="Landmark, e.g. Near Benz Circle" value={shop.landmark} onChange={set("landmark")} className="h-11" /><Err k="landmark" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Input data-testid="seller-city-input" placeholder="City" value={shop.city} onChange={set("city")} className="h-11" /><Err k="city" /></div>
        <div><Input data-testid="seller-state-input" placeholder="State" value={shop.state} onChange={set("state")} className="h-11" /><Err k="state" /></div>
      </div>
    </div>
  );
}
